import { useEffect, useLayoutEffect, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigationType } from 'react-router';
import { t, type MessageKey } from '../i18n/index.ts';
import { useIsDesktop } from '../lib/hooks.ts';
import { syncBrowserTimeZone } from '../lib/settings.ts';
import { useActivePlan, useStore } from '../state/store.tsx';
import { PlanSwitcher, ReadOnlyRibbon } from './PlanSwitcher.tsx';
import { BrandPin, Icon, type IconName } from './Icon.tsx';

interface NavItem {
  readonly to: string;
  readonly label: MessageKey;
  readonly icon: IconName;
}

const DESKTOP_MAIN: readonly NavItem[] = [
  { to: '/', label: 'nav.home', icon: 'home' },
  { to: '/forecast', label: 'nav.forecast', icon: 'forecast' },
  { to: '/plan', label: 'nav.plan', icon: 'plan' },
  { to: '/what-if', label: 'nav.whatIf', icon: 'whatIf' },
  { to: '/goals', label: 'nav.goals', icon: 'goals' },
];
const DESKTOP_FOOT: readonly NavItem[] = [
  { to: '/connections', label: 'nav.connections', icon: 'link' },
  { to: '/settings', label: 'nav.settings', icon: 'settings' },
];
const MOBILE_TABS: readonly NavItem[] = [
  { to: '/', label: 'nav.home', icon: 'home' },
  { to: '/forecast', label: 'nav.forecast', icon: 'forecast' },
  { to: '/plan', label: 'nav.plan', icon: 'plan' },
  { to: '/what-if', label: 'nav.whatIf', icon: 'whatIf' },
  { to: '/settings', label: 'nav.settings', icon: 'settings' },
];

export const firstName = (displayName: string | null | undefined, email?: string) =>
  (displayName?.trim().split(/\s+/)[0] ?? email?.split('@')[0] ?? '').replace(/^./, (c) => c.toUpperCase());

function SkipLink() {
  return <a className="skip-link" href="#main">{t('app.skipToContent')}</a>;
}

/** A new screen starts at the top; back and forward keep the browser's own scroll restoration. */
function useScrollToTopOnNavigate(pathname: string) {
  const navigationType = useNavigationType();
  // Before paint, so the new screen never shows a frame at the old scroll offset.
  useLayoutEffect(() => {
    if (navigationType !== 'POP') window.scrollTo(0, 0);
  }, [pathname, navigationType]);
}

export function Shell({ children }: { children: ReactNode }) {
  const desktop = useIsDesktop();
  const { me } = useStore();
  const { multiple } = useActivePlan();
  const location = useLocation();
  useScrollToTopOnNavigate(location.pathname);
  const signedIn = me !== null && me !== undefined;
  useEffect(() => {
    if (signedIn) void syncBrowserTimeZone();
  }, [signedIn]);
  const name = firstName(me?.user.displayName, me?.user.email);
  const hideTabs = location.pathname.startsWith('/what-if') || location.pathname.startsWith('/share/confirm');

  if (desktop) {
    return (
      <div className="shell shell--desktop">
        <SkipLink />
        <nav aria-label={t('nav.main')} className="sidebar">
          <div className="sidebar__brand">
            <BrandPin />
            <span>{t('app.name')}</span>
          </div>
          <PlanSwitcher variant="sidebar" />
          <ul className="sidebar__list">
            {DESKTOP_MAIN.map((item) => (
              <li key={item.to}><SideLink item={item} /></li>
            ))}
          </ul>
          <ul className="sidebar__list sidebar__list--foot">
            {DESKTOP_FOOT.map((item) => (
              <li key={item.to}><SideLink item={item} /></li>
            ))}
          </ul>
          <div className="user-chip">
            <span className="user-chip__avatar" aria-hidden="true">{name.charAt(0) || 'M'}</span>
            <span className="user-chip__text">
              <span className="user-chip__name">{name}</span>
              <span className="user-chip__badge">{t('app.privateAlpha')}</span>
            </span>
          </div>
        </nav>
        <main className="shell__content" id="main" tabIndex={-1}>
          <ReadOnlyRibbon />
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className={`shell shell--mobile ${hideTabs ? 'shell--no-tabs' : ''}`}>
      <SkipLink />
      <main className="shell__content" id="main" tabIndex={-1}>
        {multiple && <div className="plan-bar"><PlanSwitcher variant="bar" /></div>}
        <ReadOnlyRibbon />
        {children}
      </main>
      {!hideTabs && (
        <nav aria-label={t('nav.primary')} className="tabbar">
          {MOBILE_TABS.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === '/'} className="tabbar__item">
              <span className="tabbar__icon"><Icon name={item.icon} size={20} /></span>
              <span className="tabbar__label">{t(item.label)}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}

function SideLink({ item }: { item: NavItem }) {
  return (
    <NavLink to={item.to} end={item.to === '/'} className="sidebar__link">
      <Icon name={item.icon} size={18} />
      {t(item.label)}
    </NavLink>
  );
}

/** Page header used by every desktop screen: eyebrow, title, optional subtitle and actions. */
export function PageHeader({ eyebrow, title, subtitle, actions }: { eyebrow?: string; title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div className="page-header__titles">
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1 className="page-title">{title}</h1>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  );
}
